// src/utils/dateTimeUtils.js

/**
 * Utility for parsing and formatting dates/times in a consistent, locale-aware manner.
 * Future-proofed for easy locale and timezone customization.
 */

/**
 * Parse an input into a JS Date object.
 * Supports:
 *  - Date objects (returned as-is)
 *  - numbers (treated as ms since epoch)
 *  - ISO strings or other date strings
 *  - date-only strings YYYY-MM-DD
 *  - time-only strings HH:MM or HH:MM:SS or with milliseconds
 * @param {Date|number|string} input
 * @returns {Date|null} JS Date or null if invalid
 */
export function parseDate(input) {
  if (input == null) return null;
  if (input instanceof Date) {
    return isNaN(input.valueOf()) ? null : input;
  }
  if (typeof input === "number") {
    const d = new Date(input);
    return isNaN(d.valueOf()) ? null : d;
  }
  if (typeof input === "string") {
    // ISO or other parsable date-time
    const d = new Date(input);
    if (!isNaN(d.valueOf())) {
      return d;
    }
    // date-only YYYY-MM-DD
    let match = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [_, y, m, day] = match;
      const dd = new Date(Date.UTC(+y, +m - 1, +day));
      return isNaN(dd.valueOf()) ? null : dd;
    }
    // time-only HH:MM or HH:MM:SS(.fff)
    match = input.match(/^(\d{2}):(\d{2})(?::(\d{2})(\.\d{1,3})?)?$/);
    if (match) {
      const [, hr, min, sec = "00", ms = ""] = match;
      // Construct a UTC date on epoch date
      const iso = `1970-01-01T${hr}:${min}:${sec}${ms}Z`;
      const td = new Date(iso);
      return isNaN(td.valueOf()) ? null : td;
    }
  }
  return null;
}

/**
 * Format a date portion according to locale and timezone.
 * @param {Date|number|string} input
 * @param {string} [locale='en-GB'] BCP 47 language tag
 * @param {string} [timeZone] IANA timezone (defaults to runtime)
 * @returns {string|null} formatted date or null if invalid
 */
export function formatDate(input, locale = "en-GB", timeZone) {
  const d = parseDate(input);
  if (!d) return null;
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone,
  }).format(d);
}

/**
 * Format a time portion according to locale and timezone.
 * Accepts full Date or time-only string.
 * Drops any seconds or millisecond component—returns HH:MM.
 * @param {Date|number|string} input
 * @param {string} [locale='en-GB'] BCP 47 language tag
 * @param {string} [timeZone] IANA timezone (defaults to runtime)
 * @returns {string|null} formatted time (HH:MM) or null if invalid
 */
export function formatTime(input, locale = "en-GB", timeZone) {
  // If input is a time-only string, strip seconds/milliseconds and return HH:MM
  if (
    typeof input === "string" &&
    input.match(/^\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/)
  ) {
    // Keep only HH:MM
    const stripped = input.replace(/^(\d{2}:\d{2})(?::.*)?$/, "$1");
    return stripped;
  }
  const d = parseDate(input);
  if (!d) return null;
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(d);
}

/**
 * Format both date and time into separate fields.
 * @param {Date|number|string} input
 * @param {object} [options]
 * @param {string} [options.locale='en-GB']
 * @param {string} [options.timeZone]
 * @returns {{ date: string|null, time: string|null }}
 */
export function formatDateTime(input, { locale = "en-GB", timeZone } = {}) {
  return {
    date: formatDate(input, locale, timeZone),
    time: formatTime(input, locale, timeZone),
  };
}
